import { useEffect, useState } from 'react'

import { useSession } from '../../../../../contexts/SessionContext'
import { useDirectDebitStatus } from '../../../../../contexts/session/DirectDebitContext'
import { validateEmail, validatePhoneNumber } from '../../../../../validators/DetailsValidator'
import { getDirectDebitBanks } from '../../../../../services/PaymentService'

import StepHeader from '../../../../elements/direct-debit/StepHeader'
import TextInput from '../../../../elements/direct-debit/TextInput'
import PhoneInput, { PHONE_COUNTRIES } from '../../../../elements/direct-debit/PhoneInput'
import BankSelect from '../../../../elements/direct-debit/BankSelect'
import Callout from '../../../../elements/direct-debit/Callout'
import Reveal from '../../../../elements/direct-debit/Reveal'
import Button from '../../../../elements/direct-debit/Button'
import DDIcon from '../../../../icons/DDIcon'
import useSubmitMandate from '../useSubmitMandate'
import styles from './YourDetailsScreen.module.css'

const ACCOUNT_NUMBER_LENGTH = 10
const TERMS_AND_CONDITIONS_URL = 'https://www.mypaga.com/ngn-terms/'
const PRIVACY_POLICY_URL = 'https://www.mypaga.com/ngn-terms/ngn-terms#privacy-policy'

// Design iteration 3 - email, phone and bank account now live on one combined page
// (previously two screens: YourDetailsScreen then AddAccountScreen). The bank-name check
// still fires inline as the 10th account-number digit is typed, so by the time Proceed is
// tapped only the OTP is left to resolve - Proceed sends the OTP (same as before) and the
// Enter-code sheet opens over this already-completed form; initiateMandate fires from
// EnterCodeSheet once the code is verified, since all the mandate data is already collected
// here. If the backend returns no otpSessions at all (skipOTP, PE-367), there's no code to
// verify, so this screen calls submitMandate itself instead of opening an empty sheet.
const YourDetailsScreen = () => {
  const { updateSessionData, startOtpVerification, validateAccountDetails, sessionId, sessionData } = useSession()
  const {
    userEmail,
    setUserEmail,
    userPhone,
    setUserPhone,
    userAddress,
    setUserAddress,
    accountNumber,
    setAccountNumber,
    selectedBank,
    setSelectedBank,
    accountName,
    setAccountName,
    bankNameCheckStatus,
    setBankNameCheckStatus,
    detailsError,
    setDetailsError,
    sessionKey,
    setSessionReference,
    otpChannel,
    setOtpSessionId,
    openSheet,
  } = useDirectDebitStatus()

  const submitMandate = useSubmitMandate()
  const [phoneCountry, setPhoneCountry] = useState('NG')
  const [banks, setBanks] = useState([])
  const [banksLoading, setBanksLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    const fetchBanks = async () => {
      setBanksLoading(true)
      try {
        const response = await getDirectDebitBanks()
        if (response.financialInstitutions) {
          // FLAG: spec wants a 28px bank logo per row; the first iteration's bank select
          // only ever read `id`/`name` off this response, no logo field. Not mapping one
          // in - BankSelect falls back to the bank's initial. See final report.
          setBanks(response.financialInstitutions.map((b) => ({ id: b.id, name: b.name })))
        }
      } catch {
        setDetailsError('An error occurred while fetching banks')
      } finally {
        setBanksLoading(false)
      }
    }
    fetchBanks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const checkName = async () => {
      if (accountNumber.length !== ACCOUNT_NUMBER_LENGTH || !selectedBank.bankPublicId || accountName) return

      setBankNameCheckStatus('checking')
      try {
        const response = await validateAccountDetails(sessionId, {
          bankPublicId: selectedBank.bankPublicId,
          accountNumber,
          referenceNumber: sessionData?.referenceNumber,
        })
        if (response.statusCode === '0') {
          setAccountName(response.data.account_name)
          setBankNameCheckStatus('found')
        } else {
          setBankNameCheckStatus('not-found')
        }
      } catch {
        setBankNameCheckStatus('not-found')
      }
    }
    checkName()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBank.bankPublicId, accountNumber])

  // +234 is already selected via the dial-code segment, so the digits field only holds
  // the 10-digit national subscriber number (no trunk "0"). Reconstruct the full number
  // for validation/submission - the backend's validator (and the legacy single-field
  // screen it was written for) accepts either a 0-prefixed or +234-prefixed string.
  const phoneCountryDial = PHONE_COUNTRIES.find((c) => c.code === phoneCountry)?.dial ?? '+234'
  const phoneForBackend = `${phoneCountryDial}${userPhone}`
  const isEmailValid = validateEmail(userEmail)
  const isPhoneValid = validatePhoneNumber(phoneForBackend)
  const isBankValid = Boolean(selectedBank.bankPublicId)
  const isAccountValid = accountNumber.length === ACCOUNT_NUMBER_LENGTH && bankNameCheckStatus === 'found'
  const isAddressValid = userAddress.trim().length >= 5
  const isFormValid = isEmailValid && isPhoneValid && isBankValid && isAccountValid && isAddressValid

  const handleAccountNumberChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, ACCOUNT_NUMBER_LENGTH)
    setAccountNumber(digits)
    setAccountName('')
    setBankNameCheckStatus('idle')
  }

  const handleProceed = async () => {
    setTouched(true)
    setDetailsError('')
    if (!isFormValid) return

    setLoading(true)
    try {
      const updateResponse = await updateSessionData(sessionKey, {
        email: userEmail,
        phoneNumber: phoneForBackend,
      })

      if (updateResponse.statusCode !== '0') {
        setDetailsError(updateResponse.statusMessage || 'Failed to update session')
        return
      }

      setSessionReference(updateResponse.data.customer_reference)

      const otpResponse = await startOtpVerification(sessionKey, {
        verificationType: otpChannel.toUpperCase(),
        email: userEmail,
        phoneNumber: phoneForBackend,
        channel: otpChannel,
        reference: updateResponse.data.customer_reference,
      })

      if (otpResponse.statusCode !== '0') {
        setDetailsError(otpResponse.statusMessage || 'Failed to start OTP verification')
        return
      }

      const otpSession = otpResponse.data.otpSessions?.[0]
      if (otpSession) {
        setOtpSessionId(otpSession.otpSessionId)
        openSheet('enter-code')
      } else {
        // skipOTP: the backend didn't create an OTP session at all, so there's no code
        // to verify - go straight to the mandate instead of opening an empty sheet.
        await submitMandate()
      }
    } catch (err) {
      setDetailsError(err.statusMessage || 'An error occurred while starting the session')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <StepHeader
        title="Confirm it's you"
        step={1}
        description="We'll verify your account number and send an OTP to your phone number."
      />
      <div className={styles.body}>
        <div className={styles.fields}>
          <TextInput
            label='Email Address'
            required
            type='email'
            value={userEmail}
            onChange={(e) => {
              setUserEmail(e.target.value)
              setDetailsError('')
            }}
            error={touched && !isEmailValid ? 'Enter a full email address, like name@example.com' : undefined}
          />
          <PhoneInput
            label='Phone number'
            required
            country={phoneCountry}
            onCountryChange={setPhoneCountry}
            digits={userPhone}
            onDigitsChange={(digits) => {
              setUserPhone(digits)
              setDetailsError('')
            }}
            error={
              touched && !isPhoneValid ? 'Enter a 10-digit phone number, like 803 201 1234' : undefined
            }
          />
          <BankSelect
            label='Bank'
            required
            placeholder='Select your bank'
            value={selectedBank.bankPublicId}
            banks={banks}
            loading={banksLoading}
            error={
              bankNameCheckStatus === 'not-found' && accountNumber.length === ACCOUNT_NUMBER_LENGTH
                ? 'We couldn’t find this account at this bank.'
                : touched && !isBankValid
                  ? 'Choose your bank'
                  : undefined
            }
            onChange={(bankId) => {
              const bank = banks.find((b) => b.id === bankId)
              setSelectedBank({ bankPublicId: bankId, bankName: bank?.name || '' })
              setAccountName('')
              setBankNameCheckStatus('idle')
            }}
          />
          <TextInput
            label='Account number'
            required
            inputMode='numeric'
            maxLength={ACCOUNT_NUMBER_LENGTH}
            value={accountNumber}
            onChange={handleAccountNumberChange}
            disabled={bankNameCheckStatus === 'checking'}
            success={bankNameCheckStatus === 'found'}
            error={
              bankNameCheckStatus === 'not-found'
                ? 'Check the number and bank.'
                : touched && accountNumber.length !== ACCOUNT_NUMBER_LENGTH
                  ? 'Enter your 10-digit account number'
                  : undefined
            }
          />
          <Reveal show={bankNameCheckStatus === 'checking'}>
            <Callout variant='info'>
              <DDIcon name='spinner' />
              Checking the name with {selectedBank.bankName}…
            </Callout>
          </Reveal>
          <Reveal show={bankNameCheckStatus === 'found'}>
            <Callout variant='account-name' accountNameValue={accountName} />
          </Reveal>
          <TextInput
            label='Address'
            required
            placeholder='House number and street'
            value={userAddress}
            onChange={(e) => {
              setUserAddress(e.target.value)
              setDetailsError('')
            }}
            error={touched && !isAddressValid ? 'Enter your address' : undefined}
          />
        </div>
        {detailsError && <Callout variant='error'>{detailsError}</Callout>}
        <div className={styles.bottom}>
          <p className={styles.terms}>
            By clicking "proceed" you agree to our{' '}
            <a className={styles.termsLink} href={TERMS_AND_CONDITIONS_URL} target='_blank' rel='noopener noreferrer'>
              Terms and Conditions
            </a>{' '}
            and{' '}
            <a className={styles.termsLink} href={PRIVACY_POLICY_URL} target='_blank' rel='noopener noreferrer'>
              Privacy Policy.
            </a>
          </p>
          <Button onClick={handleProceed} loading={loading} disabled={touched && !isFormValid}>
            Proceed
          </Button>
        </div>
      </div>
    </>
  )
}

export default YourDetailsScreen
